import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Divider, Typography } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import ListTableWrapper from '@sharedCR/common/ListTableWrapper';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import {
  CreditReportStatus,
  LienPriorityType,
  LoanPurposeType,
  LoanType,
  TANGIBLE_NET_BENEFIT_STATES,
  TangibleNetBenefitReasonType,
} from 'constants/loanApplication';
import { governmentRefinancePurposeOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePrevious from 'hooks/usePrevious';
import {
  checkIsChattelLoanPurpose,
  checkIsGovernmentRefinancePurposeVisible,
  checkIsPurchaseFlow,
  checkIsRefinanceCashOutSubflow,
  checkIsRefinanceFlow,
  getDefaultGovernmentRefinancePurposeOption,
  getDefaultRefinancePurposeOption,
  getGovernmentRefinancePurposeOptionByRefinancePurpose,
  getIsAllowedGovernmentRefinancePurposeValue,
  getIsAllowedRefinancePurposeValue,
} from 'utils/loanApplication';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { getLeadSourcesAction } from 'store/companies/actions';
import { selectCompanyLeadSourcesOptions } from 'store/companies/selectors';
import {
  selectChartLegend,
  selectChattelDetails,
  selectCreditReportStatus,
  selectHousingFinanceAgenciesOptions,
  selectIsLoanLocked,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsLoanNoPpeEngineType,
  selectIsOBEngineType,
  selectLoanApplicationSubjectPropertyState,
  selectLoanFees,
} from 'store/loanApplications/selectors';
import { selectLoanLienPriorityOptions } from 'store/loanApplications/selectors/lienPriorities.selectors';
import { getBuyDownsAction, getProgramIndexTypesAction } from 'store/programs/actions';
import { selectBuyDownsValues, selectProgramIndexTypesOptions } from 'store/programs/selectors';
import {
  getImpoundTypesAction,
  getInvestorFeatureCodesAction,
  getProgramIdentifiersAction,
} from 'store/system/actions';
import {
  selectInvestorFeatureCodesOptions,
  selectProgramIdentifiersOptions,
} from 'store/system/selectors';

import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { ContentHeadline } from '../sharedComponents';

import EscrowsBlock from './EscrowsBlock';
import { useChattelDownPaymentSync } from './hooks/useChattelDownPaymentSync';
import ListTable from './ListTable';
import ListTableSkeleton from './ListTableSkeleton';
import {
  getAdditionalLoanInformationConfig,
  getDocumentVerificationTypesConfig,
  getLoanDetailsConfig,
  getNonQmLoanInfoConfig,
  getProgramDetailsRows,
  loanInfoConfigFields,
} from './LoanInfo.config';
import {
  CHATTEL_REFINANCE_BASE_LOAN_AMOUNT_CAPTION,
  DEBOUNCE_DELAY_MS,
  DOWN_PAYMENT_SYNC_SOURCE,
  DownPaymentFieldName,
  DSCR_CALCULATED_CAPTION,
  TOTAL_LOAN_AMOUNT_FEES_KEY,
} from './LoanInfo.constants';
import {
  chattelRefinanceCaptionStyles,
  dividerStyles,
  dscrCaptionStyles,
  loanDetailsStyles,
  programDetailsCustomStyles,
  programDetailsWidthStyles,
  topSectionCustomStyles,
  topSectionStyles,
} from './LoanInfo.styles';
import {
  checkIsChattelRefinance,
  checkIsPrepaymentPenaltyChoiceVisible,
  getChattelContractAmount,
  getChattelFinancedCosts,
  getDownPaymentSyncSource,
  getTotalLoanAmount,
  resolvePurchaseDownPaymentSync,
} from './LoanInfo.utils';
import LoanProgramSection from './LoanProgramSection';
import {
  checkIsLoanProgramIndexRequired,
  getLoanProgramEntityConfig,
} from './LoanProgramSection.config';

const LoanInfo = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  className,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  applicationData,
  setForm,
  setConfig,
  entitySideData,
}) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();
  const { applicationId } = useParams();

  const programIdentifiersOptions = useSelector(selectProgramIdentifiersOptions);
  const investorFeatureCodesOptions = useSelector(selectInvestorFeatureCodesOptions);
  const leadSourcesOptions = useSelector(selectCompanyLeadSourcesOptions);
  const isLoanNoPpeEngineType = useSelector(selectIsLoanNoPpeEngineType);
  const canEditLoanProgram = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_EDIT_LOAN_PROGRAM),
  );
  const loanFees = useSelector(selectLoanFees);
  const housingFinanceAgenciesOptions = useSelector(selectHousingFinanceAgenciesOptions, _.isEqual);
  const creditReportStatus = useSelector(selectCreditReportStatus);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const isOBEngineType = useSelector(selectIsOBEngineType);
  const lienPriorityOptions = useSelector(selectLoanLienPriorityOptions);
  const subjectPropertyState = useSelector(selectLoanApplicationSubjectPropertyState);
  const buyDownOptions = useSelector(selectBuyDownsValues, _.isEqual);
  const indexTypesOptions = useSelector(selectProgramIndexTypesOptions, _.isEqual);
  const { purchasePrice } = useSelector(selectChartLegend);
  const isChattelLoanSummary = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const chattelDetails = useSelector(selectChattelDetails);

  const mortgageType = isLoanNoPpeEngineType
    ? form.mortgageType?.value
    : applicationData.mortgageType;

  const {
    loanWorkflow,
    baseLoanAmount: applicationDataLoanAmount,
    downPaymentValue: applicationDataDownPaymentValue,
    totalLoanAmount: applicationDataTotalLoanAmount,
    requireEscrow: isEscrowDisabled,
    escrowSettings,
    governmentRefinancePurpose,
  } = applicationData;

  const formLoanAmount = form.baseLoanAmount;
  const formTotalLoanAmount = form.totalLoanAmount;
  const isOtherHousingFinanceAgency = form.housingFinanceAgencyId?.isOther;
  const nationalHousingActSection = form.nationalHousingActSection?.value;
  const loanPurpose = form.loanPurpose?.value;
  const refinancePurpose = form.refinancePurpose?.value;
  const tangibleNetBenefitReason = form.tangibleNetBenefitReason?.value;
  const { downPaymentValue, downPaymentPercent } = form;
  const isIndexRequired = checkIsLoanProgramIndexRequired({ form });
  const isPrepaymentPenaltyVisible = checkIsPrepaymentPenaltyChoiceVisible({
    form,
    applicationData,
  });
  const isRefinanceCashOutSubflow = checkIsRefinanceCashOutSubflow({ refinancePurpose });
  const isTangibleNetBenefitVisible =
    checkIsRefinanceFlow(loanPurpose) && TANGIBLE_NET_BENEFIT_STATES.includes(subjectPropertyState);
  const prevLoanTypeValue = usePrevious(loanPurpose);
  const prevRefinancePurpose = usePrevious(refinancePurpose);
  const prevIsTangibleNetBenefitVisible = usePrevious(isTangibleNetBenefitVisible);
  const isLoanTypeChanged = !!prevLoanTypeValue && prevLoanTypeValue !== loanPurpose;
  const isRefinanceFlow = checkIsRefinanceFlow(loanPurpose);
  const isPurchaseFlow = checkIsPurchaseFlow(loanPurpose);
  // LEN-127: a chattel loan with a sales price derives its loan amount from the contract (base = contract -
  // down payment); the down payment is paid against the Total Sales Price, which is its % basis. The saved
  // chattel state only counts while the purpose picked in the form is still the loan's.
  const isChattelLoan =
    checkIsChattelLoanPurpose(loanPurpose) ||
    (isChattelLoanSummary && loanPurpose === applicationData.loanPurpose);
  const chattelContractAmount = isChattelLoan ? getChattelContractAmount(chattelDetails) : null;
  const isChattelSalesContract = !_.isNil(chattelContractAmount);
  // LEN-205: a chattel refinance takes its base loan amount from the home-lien payoff and finances the same fees
  // and prepaids on top of it, so it shows the cent-precise base and the backend's Total Loan Amount too.
  const isChattelRefinance = isChattelLoan && checkIsChattelRefinance(chattelDetails);
  const isChattelFinanced = isChattelSalesContract || isChattelRefinance;
  const chattelFinancedCosts = getChattelFinancedCosts({
    baseLoanAmount: applicationDataLoanAmount,
    totalLoanAmount: applicationDataTotalLoanAmount,
  });
  const chattelTotalSalesPrice = isChattelSalesContract
    ? chattelContractAmount + chattelFinancedCosts
    : null;
  const shouldRecalculateCashOutAmount =
    isRefinanceCashOutSubflow &&
    creditReportStatus === CreditReportStatus.OK &&
    Number(formLoanAmount) !== Number(applicationDataLoanAmount);

  const [shouldShowTotalLoanAmount, setShouldShowTotalLoanAmount] = useState(true);
  const totalLoanAmountResultRef = useRef(null);
  const previousBusyLienPriorityTypesRef = useRef([]);

  const busyLienPriorityTypes = useMemo(() => {
    const newBusyLienPriorityTypes =
      Object.entries(form.additionalMortgages ?? applicationData.additionalMortgages ?? {}).reduce(
        (acc, [fieldKey, { lienPriorityType }]) => {
          const isDeleted = !!(
            entitySideData.additionalMortgages?.deletedAdditionalMortgages?.includes(fieldKey) ||
            applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys?.includes(fieldKey)
          );

          const lienPriorityTypeValue = lienPriorityType?.value ?? lienPriorityType;

          if (lienPriorityTypeValue === LienPriorityType.OTHER || isDeleted) {
            return acc;
          }

          acc.push(lienPriorityTypeValue);

          return acc;
        },
        [],
      ) ?? [];

    const previousBusyLienPriorityTypes = previousBusyLienPriorityTypesRef.current;

    if (_.isEqual(previousBusyLienPriorityTypes, newBusyLienPriorityTypes)) {
      return previousBusyLienPriorityTypes;
    }

    previousBusyLienPriorityTypesRef.current = newBusyLienPriorityTypes;

    return newBusyLienPriorityTypes;
  }, [
    applicationData.additionalMortgages,
    applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys,
    entitySideData.additionalMortgages?.deletedAdditionalMortgages,
    form.additionalMortgages,
  ]);

  const mortgageLienPriorityOptions = useMemo(() => {
    return lienPriorityOptions.filter(({ value }) => {
      if (value === LienPriorityType.OTHER) {
        return true;
      }

      return !busyLienPriorityTypes.includes(value);
    });
  }, [busyLienPriorityTypes, lienPriorityOptions]);

  const { insuranceEscrowSettings } = escrowSettings || {};
  const { insuranceImpoundsEscrowed } = insuranceEscrowSettings || {};

  const insuranceEscrowIds = useMemo(
    () =>
      (insuranceImpoundsEscrowed || []).map(({ applicationInsuranceId }) => applicationInsuranceId),
    [insuranceImpoundsEscrowed],
  );

  useEffectSafeStrictMode(() => {
    Promise.all([
      dispatch(getProgramIdentifiersAction()),
      dispatch(getInvestorFeatureCodesAction()),
      dispatch(getLeadSourcesAction({ applicationId })),
      dispatch(getImpoundTypesAction({ includeDeleted: true })),
      dispatch(getBuyDownsAction()),
      dispatch(getProgramIndexTypesAction()),
    ]).catch(Error);
  }, [dispatch, loanId, applicationId]);

  useEffect(() => {
    if (!isRefinanceCashOutSubflow) {
      return;
    }

    const valueToSet = shouldRecalculateCashOutAmount ? '' : undefined;

    setInForm(loanInfoConfigFields.cashOutAmount.nameInModel, valueToSet, false);
  }, [isRefinanceCashOutSubflow, shouldRecalculateCashOutAmount, setInForm]);

  // Only clear on a REAL visible->hidden transition (prevIsTangibleNetBenefitVisible === true), not
  // on mount (prevIsTangibleNetBenefitVisible === undefined): these fields are conditionally absent
  // from both the config and initialFormValues on a Purchase loan (see getLoanDetailsConfig), so
  // clearing unconditionally on mount added them to currentForm with no initialForm counterpart --
  // a phantom key-set mismatch that made checkIsFormChanged report the form as dirty as soon as the
  // user touched ANY field, even one that never persists (HZ-3922 down payment $/%).
  useEffect(() => {
    if (isTangibleNetBenefitVisible || !prevIsTangibleNetBenefitVisible) {
      return;
    }

    setMultipleInForm(
      {
        [loanInfoConfigFields.tangibleNetBenefitReason.nameInModel]: undefined,
        [loanInfoConfigFields.tangibleNetBenefitCurrentInterestRate.nameInModel]: undefined,
        [loanInfoConfigFields.tangibleNetBenefitCurrentAmortizationPeriodMonths.nameInModel]:
          undefined,
        [loanInfoConfigFields.tangibleNetBenefitDescription.nameInModel]: undefined,
      },
      false,
    );
  }, [isTangibleNetBenefitVisible, prevIsTangibleNetBenefitVisible, setMultipleInForm]);

  useEffect(() => {
    if (!tangibleNetBenefitReason) {
      return;
    }

    if (tangibleNetBenefitReason !== TangibleNetBenefitReasonType.LOWER_INTEREST_RATE) {
      setInForm(
        loanInfoConfigFields.tangibleNetBenefitCurrentInterestRate.nameInModel,
        undefined,
        false,
      );
    }

    if (
      tangibleNetBenefitReason !==
      TangibleNetBenefitReasonType.BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD
    ) {
      setInForm(
        loanInfoConfigFields.tangibleNetBenefitCurrentAmortizationPeriodMonths.nameInModel,
        undefined,
        false,
      );
    }

    if (tangibleNetBenefitReason !== TangibleNetBenefitReasonType.OTHER) {
      setInForm(loanInfoConfigFields.tangibleNetBenefitDescription.nameInModel, undefined, false);
    }
  }, [tangibleNetBenefitReason, setInForm]);

  // HZ-3922: on a loan, DownPaymentValue is backend-derived as purchasePrice - loanAmount, so it
  // can't persist independently -- the three satisfy purchasePrice = downPayment + loanAmount.
  // Editing down payment ($ or %) moves Loan Amount (purchase price fixed) so the change persists;
  // editing Loan Amount re-derives down payment + %. downPaymentPercent stays a UI-only field.
  const downPaymentSyncRef = useRef(null);

  useChattelDownPaymentSync({
    isChattelSalesContract,
    contractAmount: chattelContractAmount,
    totalSalesPrice: chattelTotalSalesPrice,
    financedCosts: chattelFinancedCosts,
    downPaymentValue,
    downPaymentPercent,
    formLoanAmount,
    formTotalLoanAmount,
    setMultipleInForm,
  });

  useEffect(() => {
    if (isChattelSalesContract) {
      downPaymentSyncRef.current = null;

      return;
    }

    if (!isPurchaseFlow) {
      downPaymentSyncRef.current = null;

      if (!_.isNil(downPaymentValue) || !_.isNil(downPaymentPercent)) {
        setMultipleInForm({ downPaymentValue: undefined, downPaymentPercent: undefined }, false);
      }

      return;
    }

    const prevSync = downPaymentSyncRef.current;

    // The down-payment fields are added to the config only for a Purchase or a chattel sales
    // contract (getConditionalArray), so getConfigInfo never seeds their value into
    // initialFormValues on the first render -- unlike
    // always-present fields such as baseLoanAmount. Back-fill the persisted $ from applicationData
    // the first time this runs so a loaded down payment isn't lost; the % then derives from it.
    if (!prevSync && _.isNil(downPaymentValue) && !_.isNil(applicationDataDownPaymentValue)) {
      setInForm(DownPaymentFieldName.AMOUNT, applicationDataDownPaymentValue, false);

      return;
    }

    const hasDownPaymentValueChanged = !!prevSync && prevSync.downPaymentValue !== downPaymentValue;
    const hasDownPaymentPercentChanged =
      !!prevSync && prevSync.downPaymentPercent !== downPaymentPercent;
    const hasLoanAmountChanged = !!prevSync && prevSync.baseLoanAmount !== formLoanAmount;
    const hasPurchasePriceChanged = !!prevSync && prevSync.purchasePrice !== purchasePrice;

    if (
      prevSync &&
      !hasDownPaymentValueChanged &&
      !hasDownPaymentPercentChanged &&
      !hasLoanAmountChanged &&
      !hasPurchasePriceChanged
    ) {
      return;
    }

    // Only a down-payment edit moves Loan Amount; a Loan Amount edit (or mount) re-derives down
    // payment + % off the loan without writing loanAmount back, so opening the form never dirties it.
    const source = getDownPaymentSyncSource({
      hasDownPaymentPercentChanged,
      hasDownPaymentValueChanged,
    });

    const nextSync = resolvePurchaseDownPaymentSync({
      source,
      purchasePrice,
      downPaymentValue,
      downPaymentPercent,
      baseLoanAmount: formLoanAmount,
    });

    const drivesLoanAmount =
      source === DOWN_PAYMENT_SYNC_SOURCE.PERCENT ||
      source === DOWN_PAYMENT_SYNC_SOURCE.DOWN_PAYMENT;

    const updates = {};

    if (nextSync.downPaymentValue !== downPaymentValue) {
      updates[DownPaymentFieldName.AMOUNT] = nextSync.downPaymentValue;
    }

    if (nextSync.downPaymentPercent !== downPaymentPercent) {
      updates[DownPaymentFieldName.PERCENT] = nextSync.downPaymentPercent;
    }

    if (drivesLoanAmount && nextSync.baseLoanAmount !== formLoanAmount) {
      updates[loanInfoConfigFields.baseLoanAmount.nameInModel] = nextSync.baseLoanAmount;
    }

    downPaymentSyncRef.current = {
      purchasePrice,
      downPaymentValue: nextSync.downPaymentValue,
      downPaymentPercent: nextSync.downPaymentPercent,
      baseLoanAmount: drivesLoanAmount ? nextSync.baseLoanAmount : formLoanAmount,
    };

    if (!_.isEmpty(updates)) {
      setMultipleInForm(updates, false);
    }
  }, [
    isChattelSalesContract,
    isPurchaseFlow,
    purchasePrice,
    downPaymentValue,
    downPaymentPercent,
    formLoanAmount,
    applicationDataDownPaymentValue,
    setInForm,
    setMultipleInForm,
  ]);

  const omitTotalLoanAmountFromConfig = useCallback(() => {
    setConfig(prev => {
      const targetGroupIndex = prev.groups.findIndex(({ key }) => key === 'loan-details');

      if (targetGroupIndex === -1) {
        return prev;
      }

      const totalLoanAmountFieldIndex = prev.groups[targetGroupIndex].fields.findIndex(
        ({ nameInModel }) => nameInModel === 'totalLoanAmount',
      );

      const updatedConfig = _.cloneDeep(prev);

      _.pullAt(updatedConfig.groups[targetGroupIndex].fields, totalLoanAmountFieldIndex);

      return updatedConfig;
    });
  }, [setConfig]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateTotalLoanAmount = useCallback(
    _.throttle(
      ({ loanFees, feesKey }) => {
        setForm(prev => {
          if (prev.baseLoanAmount === undefined) {
            updateTotalLoanAmount({ loanFees, feesKey });

            return prev;
          }

          const totalLoanAmount = getTotalLoanAmount({
            loanAmount: prev.baseLoanAmount,
            loanFees,
            feesKey,
          });

          const shouldShow = prev.baseLoanAmount !== totalLoanAmount;

          totalLoanAmountResultRef.current = { shouldShow, totalLoanAmount };

          if (!shouldShow) {
            return _.omit(prev, 'totalLoanAmount');
          }

          return {
            ...prev,
            totalLoanAmount,
          };
        });

        const result = totalLoanAmountResultRef.current;

        if (result) {
          totalLoanAmountResultRef.current = null;

          setShouldShowTotalLoanAmount(result.shouldShow);

          if (!result.shouldShow) {
            omitTotalLoanAmountFromConfig();
          }
        }
      },
      DEBOUNCE_DELAY_MS,
      { leading: true, trailing: true },
    ),
    [setInForm, setForm, omitTotalLoanAmountFromConfig],
  );

  const applyDefaultLienPriority = useCallback(() => {
    const lienPriorityType =
      loanPurpose === LoanPurposeType.ADDITIONAL_LOAN
        ? LienPriorityType.SECOND
        : LienPriorityType.FIRST;
    setInForm(
      'lienPriority',
      lienPriorityOptions.find(({ value }) => value === lienPriorityType),
      true,
    );
  }, [loanPurpose, setInForm, lienPriorityOptions]);

  useEffect(() => {
    if (applicationData.lienPriority || !loanPurpose) {
      return;
    }

    applyDefaultLienPriority();
  }, [applicationData.lienPriority, loanPurpose, applyDefaultLienPriority]);

  useEffect(() => {
    if (!isLoanTypeChanged || !loanPurpose) {
      return;
    }

    applyDefaultLienPriority();
  }, [isLoanTypeChanged, loanPurpose, applyDefaultLienPriority]);

  useEffect(() => {
    if (!checkIsGovernmentRefinancePurposeVisible(mortgageType, loanPurpose)) {
      return;
    }

    const isFHAorVA = mortgageType === LoanType.FHA || mortgageType === LoanType.VA;
    const newField = isFHAorVA ? governmentRefinancePurposeOptions[0] : null;

    const isAllowedValue = getIsAllowedGovernmentRefinancePurposeValue(
      mortgageType,
      governmentRefinancePurpose,
    );

    if (isLoanTypeChanged || !governmentRefinancePurpose || !isAllowedValue) {
      setInForm(loanInfoConfigFields.governmentRefinancePurpose.nameInModel, newField, true);
    }
  }, [
    isLoanTypeChanged,
    isRefinanceFlow,
    setInForm,
    governmentRefinancePurpose,
    mortgageType,
    loanPurpose,
  ]);

  useEffect(() => {
    if (
      !isRefinanceFlow ||
      !refinancePurpose ||
      getIsAllowedRefinancePurposeValue(mortgageType, refinancePurpose)
    ) {
      return;
    }

    setInForm(
      loanInfoConfigFields.refinancePurpose.nameInModel,
      getDefaultRefinancePurposeOption(),
      true,
    );
    setInForm(
      loanInfoConfigFields.governmentRefinancePurpose.nameInModel,
      getDefaultGovernmentRefinancePurposeOption(),
      true,
    );
  }, [isRefinanceFlow, refinancePurpose, mortgageType, setInForm]);

  useEffect(() => {
    if (
      prevRefinancePurpose === refinancePurpose ||
      !checkIsGovernmentRefinancePurposeVisible(mortgageType, loanPurpose)
    ) {
      return;
    }

    const matchedOption = getGovernmentRefinancePurposeOptionByRefinancePurpose(refinancePurpose);

    if (
      !matchedOption ||
      !getIsAllowedGovernmentRefinancePurposeValue(mortgageType, matchedOption.value)
    ) {
      return;
    }

    setInForm(loanInfoConfigFields.governmentRefinancePurpose.nameInModel, matchedOption, true);
  }, [prevRefinancePurpose, refinancePurpose, mortgageType, loanPurpose, setInForm]);

  useEffectSafeStrictMode(() => {
    // A financed chattel loan's Total Loan Amount includes financed prepaids the fees list does not
    // carry; it is the backend's figure, loaded with the form, not a client-side fee sum.
    if (isLoading || isChattelFinanced) {
      return;
    }

    updateTotalLoanAmount({
      loanFees,
      feesKey: TOTAL_LOAN_AMOUNT_FEES_KEY,
    });
  }, [isLoading, isChattelFinanced, formLoanAmount, loanFees, setInForm, updateTotalLoanAmount]);

  const programDetailsRows = useMemo(() => {
    if (_.isEmpty(applicationData)) {
      return [];
    }

    return getProgramDetailsRows({ data: applicationData, pendingChanges });
  }, [applicationData, pendingChanges]);

  const {
    loanDetailsConfig,
    additionalLoanInformationConfig,
    documentVerificationTypesConfig,
    nonQmLoanInfoConfig,
    loanProgramEntityConfig,
  } = useMemo(() => {
    const loanDetailsConfig = getLoanDetailsConfig({
      loanPurpose,
      shouldShowTotalLoanAmount,
      mortgageLienPriorityOptions,
      mortgageType,
      insuranceEscrowIds,
      isRefinanceCashOutSubflow,
      shouldRecalculateCashOutAmount,
      isTangibleNetBenefitVisible,
      tangibleNetBenefitReason,
      buyDownOptions,
      isLoanLocked,
      isChattelSalesContract,
      isChattelRefinance,
    });

    const additionalLoanInformationConfig = getAdditionalLoanInformationConfig({
      programIdentifiersOptions,
      investorFeatureCodesOptions,
      leadSourcesOptions,
      isLoanNoPpeEngineType,
      loanWorkflow,
      mortgageType,
      loanPurpose,
      housingFinanceAgenciesOptions,
      isOtherHousingFinanceAgency,
      isLoanLocked,
      nationalHousingActSection,
    });

    const documentVerificationTypesConfig = getDocumentVerificationTypesConfig();
    const nonQmLoanInfoConfig = getNonQmLoanInfoConfig({ isPrepaymentPenaltyVisible });
    const loanProgramEntityConfig = getLoanProgramEntityConfig({
      indexTypesOptions,
      isIndexRequired,
    });

    return {
      loanDetailsConfig,
      additionalLoanInformationConfig,
      documentVerificationTypesConfig,
      nonQmLoanInfoConfig,
      loanProgramEntityConfig,
    };
  }, [
    isPrepaymentPenaltyVisible,
    programIdentifiersOptions,
    leadSourcesOptions,
    investorFeatureCodesOptions,
    isLoanNoPpeEngineType,
    loanPurpose,
    loanWorkflow,
    shouldShowTotalLoanAmount,
    mortgageLienPriorityOptions,
    mortgageType,
    insuranceEscrowIds,
    housingFinanceAgenciesOptions,
    isOtherHousingFinanceAgency,
    nationalHousingActSection,
    isRefinanceCashOutSubflow,
    shouldRecalculateCashOutAmount,
    isLoanLocked,
    isTangibleNetBenefitVisible,
    tangibleNetBenefitReason,
    buyDownOptions,
    indexTypesOptions,
    isIndexRequired,
    isChattelSalesContract,
    isChattelRefinance,
  ]);

  useEffect(() => {
    if (
      !loanDetailsConfig ||
      !additionalLoanInformationConfig ||
      !documentVerificationTypesConfig ||
      !loanProgramEntityConfig
    ) {
      return;
    }

    const mergedConfig = {
      groups: [
        ...loanDetailsConfig.groups,
        ...additionalLoanInformationConfig.groups,
        ...documentVerificationTypesConfig.groups,
        ...nonQmLoanInfoConfig.groups,
        ...loanProgramEntityConfig.groups,
      ],
    };

    setEntityConfig(mergedConfig);
  }, [
    additionalLoanInformationConfig,
    loanDetailsConfig,
    documentVerificationTypesConfig,
    nonQmLoanInfoConfig,
    loanProgramEntityConfig,
    setEntityConfig,
  ]);

  if (
    !loanDetailsConfig ||
    !additionalLoanInformationConfig ||
    !documentVerificationTypesConfig ||
    !loanProgramEntityConfig
  ) {
    return null;
  }

  return (
    <Box className={className}>
      <ContentHeadline title="Loan Info" />

      <Box sx={topSectionStyles}>
        {isLoading && !isSaving ? (
          <ListTableSkeleton
            customStyles={programDetailsWidthStyles}
            firstCellWidth="100px"
            rowsNumber={6}
          />
        ) : (
          <ListTable
            isFullWidthDivider
            customStyles={programDetailsCustomStyles}
            rows={programDetailsRows}
            title="Program Details"
            titleVariant="bold16"
          />
        )}

        <ListTableWrapper
          isFullWidthDivider
          customStyles={topSectionCustomStyles}
          title="Loan Details"
          titleVariant="bold16"
        >
          <Box sx={loanDetailsStyles}>
            <FormGenerator
              changedFields={changedFields}
              config={loanDetailsConfig}
              disabledPendingChangesHighlight={disabledPendingChangesHighlight}
              form={form}
              formName={FormNames.LOAN_DETAILS}
              handleChange={handleChange}
              isLoading={isSaving || isLoading}
              numberOfColumns={2}
              pendingChanges={pendingChanges}
              setInForm={setInForm}
              staticValidateField={staticValidateField}
              validateField={validateField}
              validationErrors={validationErrors}
            />

            {isChattelRefinance && (
              <Typography
                color="text.secondary"
                sx={chattelRefinanceCaptionStyles}
                variant="regular12"
              >
                {CHATTEL_REFINANCE_BASE_LOAN_AMOUNT_CAPTION}
              </Typography>
            )}

            <EscrowsBlock
              disabledPendingChangesHighlight={disabledPendingChangesHighlight}
              escrowSettings={escrowSettings}
              form={form}
              formName={FormNames.LOAN_DETAILS}
              handleChange={handleChange}
              isEscrowDisabled={isEscrowDisabled || mortgageType === LoanType.FHA}
              isLoading={isSaving || isLoading}
              pendingChanges={pendingChanges}
              setInForm={setInForm}
              validationErrors={validationErrors}
            />
          </Box>
        </ListTableWrapper>
      </Box>

      <Divider className="divider" sx={dividerStyles} />

      <Box>
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={additionalLoanInformationConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.ADDITIONAL_LOAN_INFO}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>

      <Divider className="divider" sx={dividerStyles} />

      <Box>
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={nonQmLoanInfoConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.DOCUMENT_VERIFICATION_TYPES}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Typography color="text.secondary" sx={dscrCaptionStyles} variant="regular12">
          {DSCR_CALCULATED_CAPTION}
        </Typography>
      </Box>

      {isOBEngineType && (
        <>
          <Divider className="divider" sx={dividerStyles} />

          <Box>
            <FormGenerator
              isNestedForm
              changedFields={changedFields}
              config={documentVerificationTypesConfig}
              disabledPendingChangesHighlight={disabledPendingChangesHighlight}
              form={form}
              formName={FormNames.DOCUMENT_VERIFICATION_TYPES}
              handleChange={handleChange}
              isLoading={isSaving || isLoading}
              numberOfColumns={4}
              pendingChanges={pendingChanges}
              setInForm={setInForm}
              staticValidateField={staticValidateField}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          </Box>
        </>
      )}

      {(isLoanNoPpeEngineType || canEditLoanProgram) && (
        <>
          <Divider className="divider" sx={dividerStyles} />

          <LoanProgramSection
            changedFields={changedFields}
            disabledPendingChangesHighlight={disabledPendingChangesHighlight}
            form={form}
            handleChange={handleChange}
            indexTypesOptions={indexTypesOptions}
            isIndexRequired={isIndexRequired}
            isLoading={isLoading}
            isSaving={isSaving}
            pendingChanges={pendingChanges}
            setInForm={setInForm}
            staticValidateField={staticValidateField}
            validateField={validateField}
            validationErrors={validationErrors}
          />
        </>
      )}
    </Box>
  );
};

LoanInfo.propTypes = pagesPropTypes;

export default memo(LoanInfo);
